import type { StaticDataTypeAllowedSharedProfileTabs } from "./_generated/static-data-types";
import { isStaticDataTypeAllowedSharedProfileTabs } from "./_generated/static-data-types";
import { typedKeys } from "./lodash-extra";
import { isSharedTab } from "./shared-tabs";
import type { nil } from "./type-utils";

/**
 * Whether a capture mask of a candidate transfer decides over a SATELLITE entity of the tab (a document of its own, like a note or an
 * interview feedback) rather than over candidate fields. Exhaustive on purpose: a new tab does not compile until someone decides.
 *
 * - `true`: the transfer drops the source's entities of this tab unless the mask shares the tab (the backend maps each tab to its
 *   entity classes, `CANDIDATE_TRANSFER_SATELLITE_ENTITIES`).
 * - `false`: the mask works through the candidate's own fields (the field catalog), or the tab is not about data the transfer moves
 *   (events, e-mails, process tasks, ...). Those satellites are transferred completely, as before the mask existed.
 */
export const IS_CANDIDATE_TRANSFER_SATELLITE_TAB = {
  notes: true,
  interviews: true,
  // `DOCUMENTS_TAB`, written out: a computed key loses the literal type the derived union below needs.
  attachments: true,
  educationPerformance: true,
  educationExams: true,
  educationExamResults: true,
  accountingRecords: true,
  customFields: false,
  general: false,
  qualifications: false,
  furtherEducations: false,
  experiences: false,
  languageSkills: false,
  preferences: false,
  vaccinations: false,
  insuranceAndTax: false,
  bankDetails: false,
  other: false,
  qualificationEvaluation: false,
  healthProfessionsRegister: false,
  complementaryMeasure: false,
  recognitionPath: false,
  laborMarketAdmission: false,
  qualificationMeasure: false,
  vocationalSchool: false,
  visa: false,
  rwrCard: false,
  residence: false,
  housingAcquisition: false,
  processStatus: false,
  processOverview: false,
  processTasks: false,
  internal: false,
  events: false,
  family: false,
  presentation: false,
  requiredDocument: false,
  templates: false,
  cardView: false,
  governance: false,
  emails: false,
  employers: false,
  competencies: false,
  onlineApplications: false,
  externalCharacteristics: false,
  header: false,
} as const satisfies Record<StaticDataTypeAllowedSharedProfileTabs, boolean>;

/** The tabs whose satellite entities the transfer filters, derived from {@link IS_CANDIDATE_TRANSFER_SATELLITE_TAB}. */
export type CandidateTransferSatelliteTab = {
  [TTab in keyof typeof IS_CANDIDATE_TRANSFER_SATELLITE_TAB]: (typeof IS_CANDIDATE_TRANSFER_SATELLITE_TAB)[TTab] extends true
    ? TTab
    : never;
}[keyof typeof IS_CANDIDATE_TRANSFER_SATELLITE_TAB];

export const CANDIDATE_TRANSFER_SATELLITE_TABS: CandidateTransferSatelliteTab[] = typedKeys(
  IS_CANDIDATE_TRANSFER_SATELLITE_TAB
).filter((tab): tab is CandidateTransferSatelliteTab => IS_CANDIDATE_TRANSFER_SATELLITE_TAB[tab]);

export function isCandidateTransferSatelliteTab(tab: string | nil): tab is CandidateTransferSatelliteTab {
  return (CANDIDATE_TRANSFER_SATELLITE_TABS as string[]).includes(tab);
}

/**
 * The tabs whose mask entry would change nothing about a transfer: they govern neither candidate fields (the backend's field catalog) nor
 * satellites the transfer filters; their content is transferred completely, or they hold no data of their own. The transfer editor leaves
 * them out. Pinned down against the catalog by the backend's `candidate-transfer-mask.invariant.spec.ts`.
 */
export const CANDIDATE_TRANSFER_TABS_WITHOUT_EFFECT: StaticDataTypeAllowedSharedProfileTabs[] = [
  "cardView",
  "emails",
  "events",
  "header",
  "onlineApplications",
  "processOverview",
  "processTasks",
  "requiredDocument",
  "templates",
];

/**
 * The mask a transfer starts with, the same as the old default without the "internal notes" and "interview data" checkboxes: every tab
 * except these, ...
 */
export const CANDIDATE_TRANSFER_DEFAULT_EXCLUDED_TABS: StaticDataTypeAllowedSharedProfileTabs[] = ["interviews"];

/** ... and in these tabs every field except these Candidate paths (`TabField.name`). */
export const CANDIDATE_TRANSFER_DEFAULT_EXCLUDED_FIELDS: Partial<Record<StaticDataTypeAllowedSharedProfileTabs, string[]>> =
  {
    internal: ["internal.notes"],
  };

/**
 * Whether the default mask leaves out the field at `path` of `tab`: an excluded path and everything below it. The catalog lists parts of a
 * note on their own (`internal.notes.contents` next to `internal.notes`), and keeping one keeps the whole note.
 */
export function isCandidateTransferDefaultExcludedField(tab: string, path: string | nil): boolean {
  if (!isStaticDataTypeAllowedSharedProfileTabs(tab) || !path) {
    return false;
  }

  const excludedPaths = CANDIDATE_TRANSFER_DEFAULT_EXCLUDED_FIELDS[tab] ?? [];
  return excludedPaths.some((excluded) => path === excluded || path.startsWith(`${excluded}.`));
}

/** What {@link isTransferPrefillSource} reads of a sharing or of a sharing preset's sharing. Plain strings for the enums, so the
 *  backend classes and the frontend's generated fragments both satisfy it. */
export interface TransferPrefillSourceLike {
  sharingType?: string | nil;
  restrictionMode?: string | nil;
  profileAccessId?: string | nil;
}

/**
 * Whether a sharing (or preset) can prefill a transfer's mask: only one that restricts through its own tabs. A capture mask (`Mask`, or a
 * `profileAccessId` from before the restriction mode existed) decides per status and function, which a one-off transfer cannot take
 * over, and a View sharing has no tab model at all.
 */
export function isTransferPrefillSource(source: TransferPrefillSourceLike | nil): boolean {
  if (!source) {
    return false;
  }
  if (source.sharingType === "View") {
    return false;
  }
  if (source.restrictionMode === "Mask") {
    return false;
  }

  return !source.profileAccessId;
}

/**
 * Tabs a mask can only share together with another: the key needs every tab listed for it. An exam result belongs to an exam, and the
 * target gets the exam only when `educationExams` is shared, so results without it would have nothing to hang on.
 */
export const CANDIDATE_TRANSFER_REQUIRED_TABS: Partial<
  Record<StaticDataTypeAllowedSharedProfileTabs, StaticDataTypeAllowedSharedProfileTabs[]>
> = {
  educationExamResults: ["educationExams"],
};

/** One shared tab of a mask that lacks a tab it requires, see {@link CANDIDATE_TRANSFER_REQUIRED_TABS}. */
export interface TransferTabRequirementViolation {
  tab: StaticDataTypeAllowedSharedProfileTabs;
  missingTab: StaticDataTypeAllowedSharedProfileTabs;
}

/**
 * The shared tabs of a mask whose required tabs are not shared too. Empty for a valid mask. One function for the backend (which rejects
 * the mask) and the editor (which selects or deselects the partner tab).
 */
export function getTransferTabRequirementViolations(
  sharedTabs: readonly { key: string; fullTabPermission?: unknown; sharedFields?: readonly unknown[] | null }[] | nil
): TransferTabRequirementViolation[] {
  const sharedKeys = (sharedTabs ?? []).filter((tab) => isSharedTab(tab)).map(({ key }) => key);
  return typedKeys(CANDIDATE_TRANSFER_REQUIRED_TABS)
    .filter((tab) => sharedKeys.includes(tab))
    .flatMap((tab) =>
      (CANDIDATE_TRANSFER_REQUIRED_TABS[tab] ?? [])
        .filter((missingTab) => !sharedKeys.includes(missingTab))
        .map((missingTab): TransferTabRequirementViolation => ({ tab, missingTab }))
    );
}
